import { makeAutoObservable } from "mobx";
import { debounce } from "lodash";
import {
  BusinessManagerListItem,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
} from "@/dbot/common/commonDomain";
import { DecisionType } from "@/common/decisions";
import { StringFieldModel } from "@/common/commonForms";
import { SuggestedCompany } from "@/types/CompanyInfo";
import { TMessage } from "@/i18n/tt";

export type PreviousBusinessManager = {
  name: string;
  organisationNumber: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
};

export type ChangeOfBusinessManager = {
  type: DecisionType.changeOfBusinessManager;
  name?: string;
  organisationNumber?: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
  previousBusinessManager?: PreviousBusinessManager;
};

export class ChangeOfBusinessManagerForm {
  readonly type = DecisionType.changeOfBusinessManager;
  readonly dispatch: DbotEventDispatcher;
  readonly context: DecisionContext<ChangeOfBusinessManager>;
  readonly data?: ChangeOfBusinessManager;

  searchCompanyModel: StringFieldModel;
  searchCompanies: (input: string) => void;
  suggestedCompanies: SuggestedCompany[] = [];
  selectedCompany?: SuggestedCompany;
  currentBusinessManager: BusinessManagerListItem | undefined;
  validationError?: TMessage;

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext<ChangeOfBusinessManager>,
    data?: ChangeOfBusinessManager,
  ) {
    makeAutoObservable(this, { type: false, dispatch: false, data: false }, { autoBind: true });

    this.dispatch = dispatch;
    this.context = context;
    this.data = data;
    this.selectedCompany = mapChangeOfBusinessManagerToSuggestedCompany(data);
    this.currentBusinessManager = previousOrContextBusinessManager(context, data);
    this.searchCompanyModel = new StringFieldModel({
      value: this.selectedCompany?.name ?? "",
      required: true,
    });
    this.searchCompanies = debounce((input: string) => this.findCompanies(input), 250);
  }

  handleEvent(event: DbotEvent): void {
    switch (event.type) {
      case "dbot/search-companies-result":
        if (event.source === this) {
          this.suggestedCompanies = event.result;
        }
        break;
    }
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return this.selectedCompany !== undefined;
  }

  validate(): boolean {
    this.validationError = this.selectedCompany !== undefined ? undefined : { key: "required" };
    return this.isValid;
  }

  setSelectedCompany(company: SuggestedCompany) {
    this.searchCompanyModel.setAndCommitInputValue(company.name);
    this.selectedCompany = company;
    this.validationError = undefined;
  }

  clearSelectedCompany() {
    this.searchCompanyModel.clear();
    this.selectedCompany = undefined;
    this.validationError = undefined;
  }

  setCompanySearch(input: string) {
    this.searchCompanyModel.setInputValue(input);
    this.searchCompanies(input);
  }

  findCompanies(input: string) {
    this.dispatch([{ type: "dbot/search-companies", input, source: this }]);
  }
}

function previousOrContextBusinessManager(
  context: DecisionContext<ChangeOfBusinessManager>,
  data?: ChangeOfBusinessManager,
): BusinessManagerListItem | undefined {
  if (data?.previousBusinessManager) {
    return {
      name: data.previousBusinessManager.name,
      organisationNumber: data.previousBusinessManager.organisationNumber,
      businessAddress1: data.previousBusinessManager.businessAddress1,
      businessAddress2: data.previousBusinessManager.businessAddress2,
      businessZip: data.previousBusinessManager.businessZip,
      businessCity: data.previousBusinessManager.businessCity,
      businessCountry: data.previousBusinessManager.businessCountry,
    };
  }
  return context.contextData[context.orgNo]?.initialState?.currentBusinessManager ?? undefined;
}

export function mapChangeOfBusinessManagerToSuggestedCompany(
  data?: ChangeOfBusinessManager,
): SuggestedCompany | undefined {
  return data && data.name && data.organisationNumber
    ? {
        name: data.name,
        organizationnumber: data.organisationNumber,
        address: data.businessAddress1,
        address2: data.businessAddress2,
        zip: data.businessZip,
        city: data.businessCity,
        sourceCountry: data.businessCountry,
      }
    : undefined;
}
